import { useMemo } from 'react'
import {
  Box,
  Table,
  Text,
  Badge,
  Flex,
  IconButton,
  HStack,
  MenuRoot,
  MenuTrigger,
  MenuPositioner,
  MenuContent,
  MenuItem,
  Portal,
  Separator,
} from '@chakra-ui/react'
import { Eye, AlertTriangle, UserX, Users, MoreVertical, CreditCard, Link as LinkIcon } from 'lucide-react'
import { useOwnerAccounts } from '../hooks'
import { OWNERSHIP_TYPE_SHORT_LABELS, type OwnerBalance } from '../types'

// Type exporté pour le drawer et les modales
export interface UnitGroup {
  unitId: string
  unitNumber: string
  accountCode: string
  accountId: string | null
  owners: OwnerBalance[]
  totalBalance: number
  hasMultipleOwners: boolean
}

interface OwnerAccountsTableProps {
  residenceId: string
  showOnlyUnpaid?: boolean
  onSelectUnit?: (group: UnitGroup) => void
  onNewPayment?: (group: UnitGroup) => void
  onSendPaymentLink?: (group: UnitGroup, owner?: OwnerBalance) => void
}

export function OwnerAccountsTable({
  residenceId,
  showOnlyUnpaid = false,
  onSelectUnit,
  onNewPayment,
  onSendPaymentLink,
}: OwnerAccountsTableProps) {
  const { data: accounts, isLoading } = useOwnerAccounts(residenceId)

  // Grouper les comptes par lot
  const unitGroups = useMemo(() => {
    if (!accounts) return []

    const groupMap = new Map<string, UnitGroup>()

    for (const account of accounts) {
      const key = account.unit_id

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          unitId: account.unit_id,
          unitNumber: account.unit_number,
          accountCode: account.account_code,
          accountId: account.account_id,
          owners: [],
          totalBalance: 0,
          hasMultipleOwners: false,
        })
      }

      const group = groupMap.get(key)!
      group.owners.push(account)
      group.totalBalance += account.balance || 0
    }

    // Marquer les lots avec plusieurs propriétaires
    for (const group of groupMap.values()) {
      group.hasMultipleOwners = group.owners.length > 1
      // Trier: usufruit d'abord, puis nue-propriété, puis pleine propriété
      group.owners.sort((a, b) => {
        const order = { usufruct: 0, bare: 1, full: 2 }
        return (order[a.ownership_type || 'full'] || 2) - (order[b.ownership_type || 'full'] || 2)
      })
    }

    // Convertir en tableau et trier par numéro de lot
    const groups = Array.from(groupMap.values())
    groups.sort((a, b) => a.unitNumber.localeCompare(b.unitNumber, undefined, { numeric: true }))

    // Filtrer si showOnlyUnpaid
    if (showOnlyUnpaid) {
      return groups.filter(g => g.totalBalance > 0)
    }

    return groups
  }, [accounts, showOnlyUnpaid])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value)
  }

  const getBalanceColor = (balance: number) => {
    if (balance > 0) return 'red.600'
    if (balance < 0) return 'green.600'
    return 'gray.600'
  }

  const getBalanceBadge = (balance: number) => {
    if (balance > 0) {
      return (
        <Badge colorPalette="red" variant="subtle">
          Débiteur
        </Badge>
      )
    }
    if (balance < 0) {
      return (
        <Badge colorPalette="green" variant="subtle">
          Créditeur
        </Badge>
      )
    }
    return (
      <Badge colorPalette="gray" variant="subtle">
        Soldé
      </Badge>
    )
  }

  const getOwnershipBadge = (ownershipType: string | null, shareNum: number | null, shareDen: number | null) => {
    if (!ownershipType || ownershipType === 'full') return null

    const label = OWNERSHIP_TYPE_SHORT_LABELS[ownershipType] || ownershipType
    const fraction = shareNum && shareDen && shareDen !== 1 ? ` ${shareNum}/${shareDen}` : ''
    const color = ownershipType === 'usufruct' ? 'purple' : 'blue'

    return (
      <Badge colorPalette={color} variant="outline" size="sm">
        {label}{fraction}
      </Badge>
    )
  }

  if (isLoading) {
    return <Text>Chargement...</Text>
  }

  if (unitGroups.length === 0) {
    return (
      <Box textAlign="center" py={8} color="gray.500">
        <Text>
          {showOnlyUnpaid
            ? 'Aucun copropriétaire en impayé'
            : 'Aucun compte copropriétaire'}
        </Text>
      </Box>
    )
  }

  return (
    <Box overflowX="auto">
      <Table.Root size="sm">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>Lot</Table.ColumnHeader>
            <Table.ColumnHeader>Compte 450</Table.ColumnHeader>
            <Table.ColumnHeader>Copropriétaire(s)</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">Solde</Table.ColumnHeader>
            <Table.ColumnHeader>Statut</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">Actions</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {unitGroups.map((group) => {
            const hasOwners = group.owners.some(o => o.owner_id && o.first_name)

            return (
              <Table.Row
                key={group.unitId}
                data-owner-account-row
                cursor="pointer"
                _hover={{ bg: 'gray.50' }}
                onClick={() => onSelectUnit?.(group)}
              >
                <Table.Cell>
                  <HStack gap={2}>
                    <Text fontWeight="medium">{group.unitNumber}</Text>
                    {group.hasMultipleOwners && (
                      <Badge colorPalette="orange" variant="subtle" size="sm">
                        <Users size={12} style={{ marginRight: 4 }} />
                        {group.owners.length}
                      </Badge>
                    )}
                  </HStack>
                </Table.Cell>
                <Table.Cell fontFamily="mono" color="gray.600">
                  {group.accountCode}
                </Table.Cell>
                <Table.Cell>
                  {!hasOwners ? (
                    <Flex align="center" gap={2}>
                      <UserX size={16} color="#A0AEC0" />
                      <Text color="gray.400">Sans propriétaire</Text>
                    </Flex>
                  ) : (
                    <Flex direction="column" gap={1}>
                      {group.owners.map((owner, idx) => (
                        <Flex key={`${group.unitId}-${owner.owner_id || 'no-owner'}-${idx}`} align="center" gap={2} wrap="wrap">
                          {(owner.balance || 0) > 500 && (
                            <AlertTriangle size={14} color="#E53E3E" />
                          )}
                          <Text fontSize="sm">
                            {owner.first_name} {owner.last_name}
                          </Text>
                          {getOwnershipBadge(owner.ownership_type, owner.share_numerator, owner.share_denominator)}
                          {group.hasMultipleOwners && (
                            <Text fontSize="xs" color={getBalanceColor(owner.balance || 0)}>
                              ({formatCurrency(owner.balance || 0)})
                            </Text>
                          )}
                        </Flex>
                      ))}
                    </Flex>
                  )}
                </Table.Cell>
                <Table.Cell
                  textAlign="right"
                  fontWeight="semibold"
                  color={getBalanceColor(group.totalBalance)}
                >
                  {formatCurrency(group.totalBalance)}
                </Table.Cell>
                <Table.Cell>{getBalanceBadge(group.totalBalance)}</Table.Cell>
                <Table.Cell onClick={(e) => e.stopPropagation()}>
                  <Flex gap={1} justify="flex-end">
                    <MenuRoot positioning={{ placement: 'bottom-end' }} lazyMount unmountOnExit>
                      <MenuTrigger asChild>
                        <IconButton variant="ghost" size="sm" aria-label="Actions">
                          <MoreVertical size={16} />
                        </IconButton>
                      </MenuTrigger>
                      <Portal>
                        <MenuPositioner>
                          <MenuContent minW="200px">
                            <MenuItem value="view" onClick={() => onSelectUnit?.(group)}>
                              <HStack gap={2}>
                                <Eye size={16} />
                                <Text>Voir le compte</Text>
                              </HStack>
                            </MenuItem>
                            <MenuItem value="new-payment" onClick={() => onNewPayment?.(group)}>
                              <HStack gap={2}>
                                <CreditCard size={16} />
                                <Text>Nouveau règlement</Text>
                              </HStack>
                            </MenuItem>
                            <Separator />
                            {group.owners.length === 1 ? (
                              <MenuItem
                                value="send-link"
                                onClick={() => onSendPaymentLink?.(group, group.owners[0])}
                              >
                                <HStack gap={2}>
                                  <LinkIcon size={16} />
                                  <Text>Envoyer un lien de paiement</Text>
                                </HStack>
                              </MenuItem>
                            ) : (
                              group.owners
                                .filter((o) => o.owner_id && o.first_name)
                                .map((owner) => (
                                  <MenuItem
                                    key={owner.owner_id}
                                    value={`send-link-${owner.owner_id}`}
                                    onClick={() => onSendPaymentLink?.(group, owner)}
                                  >
                                    <HStack gap={2}>
                                      <LinkIcon size={16} />
                                      <Text fontSize="sm">
                                        Lien → {owner.first_name} {owner.last_name}
                                      </Text>
                                    </HStack>
                                  </MenuItem>
                                ))
                            )}
                          </MenuContent>
                        </MenuPositioner>
                      </Portal>
                    </MenuRoot>
                  </Flex>
                </Table.Cell>
              </Table.Row>
            )
          })}
        </Table.Body>
      </Table.Root>
    </Box>
  )
}
